import React from 'react';
import {useLocation, useRoute} from 'wouter';

import {PageLayout} from '../../../components/layout/page-layout';
import {BrowserTabTitle} from '../../../components/window-title/browser-tab-title';
import {useDocRevisionsContext} from '../../../providers/version';
import {getBrowserTabTitle} from '../common/get-browser-tab-title';
import {DocRevisionsPanel} from '../common/version-history/doc-revisions-panel';

import {Editor} from './editor/editor';
import {useCreateDocumentMutation} from './hooks/use-create-document-mutation';
import {useDocumentQuery} from './hooks/use-document-query';
import {EditModeDocumentsSidebar} from './sidebar/edit-docs-sidebar';
import {usePageLayout} from '../../../components/layout/page-layout/use-page-layout';
import {useSidebarTabs} from '../../../hooks/use-sidebar-tabs';
import {EditModeTab} from '../../../components/layout/page-layout/layout-typings';
import {useHandleFindUsages} from '../common/sidebar-search-field/hooks/use-handle-find-usages';
import {GlobalVersionData} from '../../../hooks/global-version/use-global-version-query';
import {EditModeActivityBar} from './activity-bar/activity-bar';
import {showErrorNotification} from '../../../providers/notifications-center/show-notification';
import {useSidebarSearchProps} from '../common/sidebar-search-field/hooks/use-sidebar-search-props';
import {useDocumentSidebarState} from '../common/right-sidebar/hooks/use-document-sidebar-state';
import {DocumentRouteParams} from '../interface';

interface Props {
    globalVersions: GlobalVersionData[] | null;
    documentId?: string;
    createdDocumentType?: DocType;
}

export function EditView(props: Props) {
    const [, revisionParams] = useRoute<DocumentRouteParams>('/:documentId/revisions/:revisionId?');
    const {revisionId} = revisionParams || {};

    const tabSettings = useSidebarTabs({
        layout: 'edit',
        activityTabStorageKey: 'documents.edit.lastTab',
        defaultActivityBarTab: EditModeTab.RECENT
    });
    const layoutSettings = usePageLayout({
        isHasRightPanel: !!revisionId,
        sidebarSizeStorageKey: 'documents.edit.layoutSize',
        sidebarClosedStorageKey: 'documents.edit.sidebarClosed'
    });
    const searchSidebarContext = useSidebarSearchProps({
        searchDataGlobalStateVar: 'documents.edit.search'
    });

    const findUsagesProps = useHandleFindUsages({
        tabSettings,
        searchSidebarContext,
        searchTab: EditModeTab.SEARCH,
        openSidebar: layoutSettings.openSidebar
    });

    const rightSidebarProps = useDocumentSidebarState(false);

    const [, navigate] = useLocation();

    const versionModel = useDocRevisionsContext();
    const documentQuery = useDocumentQuery(props.documentId);
    const createDocument = useCreateDocumentMutation();

    const handleCreateDocument = async (type: DocType = 'BTB') => {
        try {
            const draftDocument = await createDocument.mutateAsync({type});

            navigate(`/${draftDocument.accession}`);
        } catch (error) {
            showErrorNotification('Error: Not possible to create a draft')(error);
        }
    };

    React.useEffect(() => {
        if (props.createdDocumentType) {
            handleCreateDocument(props.createdDocumentType);
            navigate('/', {replace: true});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.createdDocumentType]);

    const closePanelLink = `/${props.documentId}`;

    const getSelectedRevisionLink = React.useCallback(
        (revision: string) => `/${props.documentId}/revisions/${revision}`,
        [props.documentId]
    );

    const getDocumentLink = React.useCallback(
        (documentId: string, nodeId?: string) =>
            `/${documentId}${nodeId ? `/@${encodeURIComponent(nodeId)}` : ''}`,
        []
    );

    return (
        <BrowserTabTitle
            text={getBrowserTabTitle(
                versionModel.selectedRevision?.documentName ?? documentQuery.data?.name
            )}
        >
            <PageLayout
                rightPanel={
                    <DocRevisionsPanel
                        docRevisionsProps={versionModel}
                        closePanelLink={closePanelLink}
                        getSelectedRevisionLink={getSelectedRevisionLink}
                        globalVersions={props.globalVersions}
                        mode="edit"
                        revisionId={revisionId}
                    />
                }
                sidebar={
                    <EditModeDocumentsSidebar
                        tabSettings={tabSettings}
                        searchSidebarContext={searchSidebarContext}
                        findUsagesProps={findUsagesProps}
                        documentId={props.documentId}
                        revisionId={revisionId}
                        getDocumentLink={getDocumentLink}
                        layoutSettings={layoutSettings}
                        rightSidebarProps={rightSidebarProps}
                    />
                }
                layoutSettings={layoutSettings}
                activityBar={
                    <EditModeActivityBar
                        isDocCreating={createDocument.isPending}
                        createNew={handleCreateDocument}
                        tabSettings={tabSettings}
                        layoutSettings={layoutSettings}
                    />
                }
            >
                {props.documentId ? (
                    <Editor
                        accession={props.documentId}
                        findUsagesProps={findUsagesProps}
                        rightSidebarProps={rightSidebarProps}
                        revisionId={revisionId}
                    />
                ) : null}
            </PageLayout>
        </BrowserTabTitle>
    );
}
